/**
 * PatiAsistan — floating cat avatar + chat panel (bottom-right).
 *
 * Three states:
 *   • idle / active  →  cat sits, ear twitches, eyes blink. Click to open chat.
 *   • thinking       →  paw waves while the question is in flight
 *   • nap            →  cat curls up, "Zzz…" floats above when daily quota is hit
 *
 * Cost-saving pipeline (server-side):
 *   dictionary → mongo cache → Gemini Flash (quota-gated). The component only
 *   knows about /api/assistant/ask + /api/assistant/status — it does not need
 *   to know how an answer was sourced.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Send, X, MessageCircle, Sparkles, Check, Camera } from "lucide-react";
import { apiClient } from "../auth";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import {
  HATS,
  NECKLACES,
  HAIRSTYLES,
  HAT_PALETTES,
  HAIR_COLORS,
  HatLayer,
  NecklaceLayer,
  HairLayer,
  AccessoryPreview,
  useAccessories,
} from "./PatiAccessories";
import { useLiveCameraShutter } from "../context/LiveCameraShutterContext";

const SUGGESTIONS_TR = [
  "Yavru kedi buldum, ne yapmalıyım?",
  "Kuduz şüphesi varsa ne yapmalı?",
  "Kediyi nasıl beslemeliyim?",
  "Kısırlaştırma nasıl yaptırılır?",
];

const SUGGESTIONS_EN = [
  "I found a kitten, what should I do?",
  "What if there's a rabies risk?",
  "How do I feed a street cat?",
  "How do I get a cat neutered?",
];

const STORAGE_KEY = "patiAsistan.history.v1";

/* ────────────────────────────────────────────────────────────────────────
 * Pati Asistan Avatar — three animated video states + behavioural mapping.
 *
 * Source videos (≈5s each, 512×512):
 *   • SLEEPING  — curled-up nap loop
 *   • WAKING    — one-shot transition from sleep → sitting
 *   • SITTING   — active/jumping loop (the "Yellow Minimalist" clip)
 *
 * Behavioural mapping (Feb 2026):
 *   • napMode === true            → SLEEPING (loop, low-saturation filter)
 *   • mounted / tab re-focused    → WAKING (plays once) → IDLE
 *   • hovered (mouse enter)       → SITTING video PLAYS (active/jumping)
 *   • idle (default)              → SITTING video PAUSED on first frame
 *
 * The sitting <video> element stays mounted for both IDLE and HOVER so
 * we toggle play/pause imperatively (no source reload → no flicker).
 *
 * White background is removed at render time via `mix-blend-mode: darken`,
 * so the kitten silhouette appears cleanly over the orange platform.
 * ──────────────────────────────────────────────────────────────────────── */

const KITTEN_VIDEOS = {
  sleeping: { webm: "/media/pati_sleeping.webm", mp4: "/media/pati_sleeping.mp4" },
  waking:   { webm: "/media/pati_waking.webm",   mp4: "/media/pati_waking.mp4"   },
  sitting:  { webm: "/media/pati_sitting.webm",  mp4: "/media/pati_sitting.mp4"  },
};

function CatAvatar({
  napMode,
  thinking,
  hovered = false,
  // Sohbet paneli açıkken kedi AYAKTA sabit dursun: sitting klibinin
  // 1.15sn'sindeki ayakta karesinde dondurulur (0-0.9sn oturma,
  // 1.0-1.4sn ayakta, sonrası boş kare — o yüzden asla oynatılmaz).
  standPose = false,
  large = false,
  accessories,
  wakeNonce = 0,
  // Optional interaction handlers — when provided, render as a <button>.
  onClick,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  ariaLabel,
  testId,
}) {
  const isInteractive = typeof onClick === "function";
  // 'sizeClass' kept commented for grep history; sizing now lives in CSS.
  const hat = accessories?.hat || "none";
  const hatPalette = accessories?.hatPalette || "combo-1";
  const necklace = accessories?.necklace || "none";
  const hair = accessories?.hair || "none";
  const hairColor = accessories?.hairColor || "white";

  // ── State machine for which clip is showing ─────────────────────────
  // "sleeping" / "waking" / "sitting"  (sitting covers both idle & hover)
  const [clip, setClip] = useState(napMode ? "sleeping" : "waking");
  // Three ALWAYS-MOUNTED <video> refs — we cross-fade them via opacity so
  // there is no src swap (and therefore no black flash on transitions).
  const sleepingRef = useRef(null);
  const wakingRef = useRef(null);
  const sittingRef = useRef(null);
  // Track previous napMode so we can play the WAKING transition on false-edge.
  const prevNapRef = useRef(napMode);
  // Track wakeNonce so re-mounts / tab-re-focus re-trigger the waking clip.
  const prevWakeRef = useRef(wakeNonce);

  // Drive clip based on napMode and wake triggers.
  useEffect(() => {
    const prevNap = prevNapRef.current;
    if (napMode) {
      setClip("sleeping");
    } else if (prevNap) {
      // false-edge of napMode → play waking once
      setClip("waking");
    }
    prevNapRef.current = napMode;
  }, [napMode]);

  // Tab focus / mount nonce → replay the waking clip.
  useEffect(() => {
    if (napMode) return;
    if (wakeNonce !== prevWakeRef.current) {
      setClip("waking");
      prevWakeRef.current = wakeNonce;
    }
  }, [wakeNonce, napMode]);

  // Imperative playback control — runs whenever clip / hovered changes.
  // Cross-fade architecture: all three videos stay mounted; we just
  // play/pause/reset and let CSS opacity handle the visual transition.
  const primeTimerRef = useRef(null);
  useEffect(() => {
    const sleep = sleepingRef.current;
    const wake = wakingRef.current;
    const sit = sittingRef.current;

    const safePlay = (v) => {
      if (!v) return;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    const safePause = (v, resetToStart = false) => {
      if (!v) return;
      try {
        v.pause();
        if (resetToStart) v.currentTime = 0;
      } catch (_) { /* noop */ }
    };

    if (clip === "sleeping") {
      safePlay(sleep);
      safePause(wake, true);
      safePause(sit, true);
    } else if (clip === "waking") {
      safePause(sleep);
      // Always restart waking from frame 0 so the transition plays in full.
      try { if (wake) wake.currentTime = 0; } catch (_) { /* noop */ }
      safePlay(wake);
      // PRIME the sitting frame WHILE waking plays: a never-played <video>
      // renders NOTHING on iOS/Android WebView, so pausing it at frame 0
      // without playing first caused the mascot to vanish for a beat when
      // the waking clip ended. Play it for ~150ms (opacity is 0 anyway),
      // then pause — the frame is now decoded and ready to fade in.
      try { if (sit) sit.currentTime = 0; } catch (_) { /* noop */ }
      safePlay(sit);
      if (primeTimerRef.current) clearTimeout(primeTimerRef.current);
      primeTimerRef.current = setTimeout(() => safePause(sit), 150);
    } else {
      // sitting — ESKİ SAKİN DAVRANIŞ: boşta SABİT KARE, hover'da oynar.
      // (Sürekli döngü kullanıcıyı rahatsız etti — geri alındı.)
      // Kare, soğuk pause yerine "oynat→150ms→durdur" (prime) ile
      // sabitlenir: WebView hiç oynatılmamış videoyu çizmediği için
      // eski koddaki görünmezlik bunun eksikliğindendi.
      safePause(sleep);
      safePause(wake);
      if (hovered && !standPose) {
        safePlay(sit);
      } else {
        // standPose: 1.15sn'deki AYAKTA karesinde dondur.
        // Normal: baştan (oturma karesi) prime et.
        try { sit.currentTime = standPose ? 1.15 : 0; } catch (_) { /* noop */ }
        safePlay(sit);
        if (primeTimerRef.current) clearTimeout(primeTimerRef.current);
        primeTimerRef.current = setTimeout(() => safePause(sit), 150);
      }
    }
    return () => {
      if (primeTimerRef.current) clearTimeout(primeTimerRef.current);
    };
  }, [clip, hovered, standPose]);

  // Waking asla takılı kalmasın: video yüklenemez de onEnded hiç ateşlenmezse
  // 6.5 sn sonra (klipler ~5 sn) oturma durumuna zorla geç.
  useEffect(() => {
    if (clip !== "waking") return;
    const tmr = setTimeout(() => setClip("sitting"), 6500);
    return () => clearTimeout(tmr);
  }, [clip]);

  // UZUN OTURUM BEKÇİSİ: WebView bellek baskısında duraklatılmış karenin
  // içeriğini boşaltabilir → maskot sessizce kayboluyordu. 20 sn'de bir
  // kontrol: kare kaybolmuşsa yeniden yükle + prime et (oynat→durdur).
  // Görünüme etkisi yok — kedi sabit karede kalır, sadece asla kaybolmaz.
  useEffect(() => {
    const id = setInterval(() => {
      if (napMode || clip !== "sitting" || (hovered && !standPose)) return;
      const sit = sittingRef.current;
      if (!sit) return;
      if (sit.readyState < 2 || sit.videoWidth === 0) {
        try { sit.load(); } catch (_) { /* noop */ }
        try { sit.currentTime = standPose ? 1.15 : 0; } catch (_) { /* noop */ }
        const p = sit.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
        setTimeout(() => { try { sit.pause(); } catch (_) { /* noop */ } }, 200);
      }
    }, 20000);
    return () => clearInterval(id);
  }, [clip, hovered, napMode, standPose]);

  // Composed CSS filter for the kitten video based on current state.
  const imgFilter = napMode
    ? "grayscale(0.5) brightness(0.85)"
    : "none";

  const RootTag = isInteractive ? "button" : "div";
  // Android WebView occasionally drops React's synthetic onClick when the
  // element is position:fixed with a transform parent. Fire a manual
  // onTouchEnd → onClick bridge so the tap registers even when synthetic
  // events misfire. We preventDefault to suppress the duplicate click
  // that would otherwise follow ~300ms later.
  const handleTouchEnd = useCallback(
    (e) => {
      if (!isInteractive || !onClick) return;
      // Only treat short, single-finger taps as clicks.
      if (e.changedTouches && e.changedTouches.length === 1) {
        e.preventDefault();
        onClick(e);
      }
    },
    [isInteractive, onClick],
  );
  const interactiveProps = isInteractive
    ? {
        type: "button",
        onClick,
        onTouchEnd: handleTouchEnd,
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur,
        "aria-label": ariaLabel,
      }
    : { "aria-hidden": true };

  return (
    <RootTag
      className={`cat-mascot-badge ${large ? "" : "cat-mascot-badge--sm"} ${
        thinking ? "pati-thinking-glow" : ""
      }`}
      data-testid={
        testId ||
        (napMode ? "pati-cat-nap" : thinking ? "pati-cat-thinking" : "pati-cat-active")
      }
      style={{
        background: napMode
          ? "linear-gradient(135deg, #f5f5f4 0%, #e7e5e4 100%)"
          : "#ffffff",
        // Safety net (Feb 2026) — explicit inline position:fixed so the
        // mascot can't accidentally fall back to static/relative if a
        // stale CSS bundle is cached on the client. Sized via the
        // `cat-mascot-badge--sm` class (small) or base class (large).
        ...(large
          ? {}
          : {
              position: "fixed",
              right: "12px",
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)",
              zIndex: 41,
            }),
      }}
      {...interactiveProps}
    >
      {/* SVG chroma-key filter moved to PatiAsistan root (sibling, not
          child) — when placed inside this button it sometimes painted a
          1-pixel dot at the badge's top-left corner on Android WebView. */}

      {/* Cat image (3 cross-fading videos) — CHILD of the circular badge.
          Clipped by the badge's overflow:hidden. */}
      <div className="cat-mascot-image" data-testid="cat-mascot-image">
        {(["sleeping", "waking", "sitting"]).map((c) => {
          const refMap = {
            sleeping: sleepingRef,
            waking: wakingRef,
            sitting: sittingRef,
          };
          const srcs = KITTEN_VIDEOS[c];
          const isActive = clip === c;
          const shouldLoop = c === "sleeping" || c === "sitting";
          return (
            <video
              key={c}
              ref={refMap[c]}
              loop={shouldLoop}
              muted
              playsInline
              preload="auto"
              disablePictureInPicture
              disableRemotePlayback
              onEnded={() => {
                if (c === "waking") setClip("sitting");
              }}
              className={`${thinking && isActive ? "pati-thinking-pulse" : ""}`}
              style={{
                position: c === "sleeping" ? "relative" : "absolute",
                inset: 0,
                width: "100%",
                height: "auto",
                opacity: isActive ? 1 : 0,
                transition: "opacity 0.3s ease-in-out, filter 0.4s ease",
                filter: `url(#patiWhiteKey) ${imgFilter !== "none" ? imgFilter : ""}`.trim(),
                // mix-blend-mode removed — on Android WebView/TWA the
                // blend region renders as a faint SQUARE artefact around
                // the circular badge. The SVG chroma-key feColorMatrix
                // already turns the white background fully transparent,
                // so the blend mode was redundant.
                pointerEvents: "none",
                background: "transparent",
                zIndex: isActive ? 2 : 1,
                WebkitBackfaceVisibility: "hidden",
                backfaceVisibility: "hidden",
                outline: "none",
                border: "none",
                WebkitTapHighlightColor: "transparent",
              }}
              data-testid={`pati-video-${c}`}
            >
              <source src={srcs.webm} type="video/webm" />
              <source src={srcs.mp4}  type="video/mp4" />
            </video>
          );
        })}
      </div>

      {/* Accessory overlay — SVG layered ABOVE the kitten image. */}
      {!napMode && (hat !== "none" || hair !== "none" || necklace !== "none") && (
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          className="absolute pointer-events-none"
          style={{
            left: "50%",
            bottom: large ? "6px" : "2px",
            width: large ? "68px" : "40px",
            height: large ? "68px" : "40px",
            transform: "translateX(-50%)",
            zIndex: 3,
          }}
          data-testid="pati-accessory-overlay"
        >
          <defs>
            <filter id="patiFurWind" x="-15%" y="-15%" width="130%" height="130%">
              <feTurbulence type="fractalNoise" baseFrequency="0.4 0.3" numOctaves="2" seed="3" result="t">
                <animate attributeName="baseFrequency" dur="7s" values="0.4 0.3;0.55 0.4;0.4 0.3" repeatCount="indefinite" />
              </feTurbulence>
              <feDisplacementMap in="SourceGraphic" in2="t" scale="0.9" xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </defs>
          <NecklaceLayer necklace={necklace} />
          <HairLayer hair={hair} colorKey={hairColor} />
          <HatLayer hat={hat} palette={hatPalette} />
        </svg>
      )}

      {/* Nap-mode Zzz drift — floating Z's in the top-right of the badge */}
      {napMode && (
        <div
          className="absolute inset-0 pointer-events-none"
          data-testid="pati-nap-zzz-overlay"
          style={{ zIndex: 4 }}
        >
          <span className="pati-zzz absolute right-3 top-4 text-stone-600 font-bold text-sm select-none">z</span>
          <span
            className="pati-zzz absolute right-6 top-2 text-stone-500 font-bold text-xs select-none"
            style={{ animationDelay: "0.55s" }}
          >
            z
          </span>
        </div>
      )}
    </RootTag>
  );
}


/* ─────────────── Customize sheet ─────────────── */
function CustomizeSheet({ lang, accessories, isPremium, onClose }) {
  const [tab, setTab] = useState("hat");
  const isTr = lang === "tr";
  const showPalette =
    tab === "hat" &&
    HATS.find((h) => h.id === accessories.hat)?.supportsPalette;

  const Card = ({ active, onClick, children, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all hover:scale-[1.03] ${
        active
          ? "border-orange-500 bg-orange-50 shadow-md"
          : "border-stone-200 bg-white hover:border-orange-200"
      }`}
    >
      {children}
      {active && (
        <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-orange-500 text-white grid place-items-center">
          <Check size={10} strokeWidth={3} />
        </span>
      )}
    </button>
  );

  return (
    <div
      className="fixed w-[380px] max-w-[calc(100vw-2.5rem)] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden cat-mascot-panel"
      style={{ height: "min(560px, calc(100vh - 17rem))", zIndex: 10000 }}
      data-testid="pati-asistan-customize-sheet"
    >
      {/* Header */}
      <div className="bg-orange-50 border-b border-orange-100 px-4 py-3 flex items-center gap-2">
        <Sparkles size={16} className="text-orange-600" />
        <div className="flex-1 font-display font-bold text-sm">
          {isTr ? "Kişiselleştir" : "Customize"}
        </div>
        <button
          onClick={onClose}
          data-testid="pati-asistan-customize-close"
          className="text-stone-500 hover:text-stone-900 rounded-full w-7 h-7 grid place-items-center hover:bg-white"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-stone-200">
        <button
          onClick={() => setTab("hat")}
          data-testid="pati-customize-tab-hat"
          className={`flex-1 py-2 text-sm font-semibold transition-colors ${
            tab === "hat"
              ? "text-orange-700 border-b-2 border-orange-500 bg-orange-50/50"
              : "text-stone-500 hover:text-stone-800"
          }`}
        >
          {isTr ? "Şapka" : "Hat"}
        </button>
        <button
          onClick={() => setTab("hair")}
          data-testid="pati-customize-tab-hair"
          className={`flex-1 py-2 text-sm font-semibold transition-colors relative ${
            tab === "hair"
              ? "text-orange-700 border-b-2 border-orange-500 bg-orange-50/50"
              : "text-stone-500 hover:text-stone-800"
          }`}
        >
          {isTr ? "Saç" : "Hair"}
          <span className="ml-1 text-[9px] uppercase tracking-wider text-amber-600 align-middle font-bold">
            ★
          </span>
        </button>
        <button
          onClick={() => setTab("necklace")}
          data-testid="pati-customize-tab-necklace"
          className={`flex-1 py-2 text-sm font-semibold transition-colors ${
            tab === "necklace"
              ? "text-orange-700 border-b-2 border-orange-500 bg-orange-50/50"
              : "text-stone-500 hover:text-stone-800"
          }`}
        >
          {isTr ? "Kolye" : "Necklace"}
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-3 bg-stone-50 space-y-4">
        {tab === "hat" && (
          <>
            <div className="grid grid-cols-3 gap-2">
              {HATS.map((h) => (
                <Card
                  key={h.id}
                  active={accessories.hat === h.id}
                  onClick={() => accessories.setHat(h.id)}
                  testId={`pati-customize-hat-${h.id}`}
                >
                  <AccessoryPreview type="hat" id={h.id} palette={accessories.hatPalette} />
                  <span className="text-[11px] font-semibold text-stone-700 text-center leading-tight">
                    {isTr ? h.labelTr : h.labelEn}
                  </span>
                  {h.rarity === "rare" && (
                    <span className="text-[9px] uppercase tracking-wider text-amber-600 font-bold">
                      {isTr ? "Özel" : "Rare"}
                    </span>
                  )}
                </Card>
              ))}
            </div>

            {showPalette && (
              <div data-testid="pati-customize-palette">
                <p className="text-xs font-semibold text-stone-600 mb-2 px-1">
                  {isTr ? "Renk Kombinasyonu" : "Color Combination"}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(HAT_PALETTES).map(([key, p]) => (
                    <button
                      key={key}
                      onClick={() => accessories.setHatPalette(key)}
                      data-testid={`pati-customize-palette-${key}`}
                      className={`flex items-center gap-2 p-2 rounded-xl border-2 transition-all ${
                        accessories.hatPalette === key
                          ? "border-orange-500 bg-orange-50 shadow-sm"
                          : "border-stone-200 bg-white hover:border-orange-200"
                      }`}
                    >
                      {/* Two-color swatch */}
                      <div className="flex shrink-0 w-9 h-9 rounded-lg overflow-hidden border border-stone-200">
                        <div className="w-1/2 h-full" style={{ background: p.primary }} />
                        <div className="w-1/2 h-full" style={{ background: p.secondary }} />
                      </div>
                      <span className="text-[11px] font-semibold text-stone-700 text-left leading-tight">
                        {isTr ? p.labelTr : p.labelEn}
                      </span>
                      {accessories.hatPalette === key && (
                        <Check size={14} className="ml-auto text-orange-600" />
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-stone-400 mt-2 px-1 leading-snug">
                  {isTr
                    ? "Renk kombinasyonları herhangi bir takıma veya kulübe ait değildir."
                    : "Color combinations are generic and not affiliated with any team."}
                </p>
              </div>
            )}
          </>
        )}

        {tab === "hair" && (
          <>
            {!isPremium && (
              <div
                className="rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 p-3 flex items-start gap-2"
                data-testid="pati-hair-premium-gate"
              >
                <Sparkles size={16} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-amber-900 leading-snug">
                    {isTr ? "Saç modelleri Premium'a özel" : "Hairstyles are Premium-only"}
                  </p>
                  <p className="text-[11px] text-amber-800 mt-0.5 leading-snug">
                    {isTr
                      ? "Pati'ye fırça atalım! Premium üyeler kürk bazlı saç modellerini seçebilir."
                      : "Give Pati a fresh look! Premium members unlock fur-based hairstyles."}
                  </p>
                  <a
                    href="/premium"
                    className="inline-block mt-1 text-[11px] font-bold text-orange-700 hover:underline"
                    data-testid="pati-hair-upgrade-link"
                  >
                    {isTr ? "Premium'a yükselt →" : "Upgrade to Premium →"}
                  </a>
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              {HAIRSTYLES.map((h) => {
                const locked = h.premium && !isPremium;
                return (
                  <button
                    key={h.id}
                    onClick={() => !locked && accessories.setHair(h.id)}
                    disabled={locked}
                    data-testid={`pati-customize-hair-${h.id}`}
                    className={`relative flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all ${
                      locked
                        ? "border-stone-200 bg-stone-100 opacity-60 cursor-not-allowed"
                        : accessories.hair === h.id
                          ? "border-orange-500 bg-orange-50 shadow-md hover:scale-[1.03]"
                          : "border-stone-200 bg-white hover:border-orange-200 hover:scale-[1.03]"
                    }`}
                  >
                    <AccessoryPreview type="hair" id={h.id} hairColor={accessories.hairColor} />
                    <span className="text-[11px] font-semibold text-stone-700 text-center leading-tight">
                      {isTr ? h.labelTr : h.labelEn}
                    </span>
                    {h.premium && (
                      <span className="text-[9px] uppercase tracking-wider text-amber-600 font-bold flex items-center gap-0.5">
                        <Sparkles size={8} /> {isTr ? "Premium" : "Premium"}
                      </span>
                    )}
                    {accessories.hair === h.id && !locked && (
                      <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-orange-500 text-white grid place-items-center">
                        <Check size={10} strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {isPremium && accessories.hair !== "none" && (
              <div data-testid="pati-hair-color-picker">
                <p className="text-xs font-semibold text-stone-600 mb-2 px-1">
                  {isTr ? "Saç Rengi" : "Hair Color"}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(HAIR_COLORS).map(([key, c]) => (
                    <button
                      key={key}
                      onClick={() => accessories.setHairColor(key)}
                      data-testid={`pati-customize-haircolor-${key}`}
                      className={`flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all ${
                        accessories.hairColor === key
                          ? "border-orange-500 bg-orange-50 shadow-sm"
                          : "border-stone-200 bg-white hover:border-orange-200"
                      }`}
                    >
                      <div
                        className="w-7 h-7 rounded-full border border-stone-300 shadow-inner"
                        style={{ background: `linear-gradient(135deg, ${c.hex} 0%, ${c.shade} 100%)` }}
                      />
                      <span className="text-[10px] font-semibold text-stone-600">
                        {isTr ? c.labelTr : c.labelEn}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {tab === "necklace" && (
          <div className="grid grid-cols-2 gap-2">
            {NECKLACES.map((n) => (
              <Card
                key={n.id}
                active={accessories.necklace === n.id}
                onClick={() => accessories.setNecklace(n.id)}
                testId={`pati-customize-necklace-${n.id}`}
              >
                <AccessoryPreview type="necklace" id={n.id} />
                <span className="text-[11px] font-semibold text-stone-700 text-center leading-tight">
                  {isTr ? n.labelTr : n.labelEn}
                </span>
                {n.rarity === "rare" && (
                  <span className="text-[9px] uppercase tracking-wider text-amber-600 font-bold">
                    {isTr ? "Özel" : "Rare"}
                  </span>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-stone-200 p-3 bg-white flex items-center justify-between">
        <p className="text-[11px] text-stone-500">
          {isTr ? "Otomatik kaydedildi" : "Auto-saved"}
        </p>
        <button
          onClick={onClose}
          data-testid="pati-customize-done"
          className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-full px-4 py-1.5 transition-colors"
        >
          {isTr ? "Tamam" : "Done"}
        </button>
      </div>
    </div>
  );
}

export default function PatiAsistan() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { active: liveShutterActive, disabled: liveShutterDisabled, triggerShutter } =
    useLiveCameraShutter();
  const [open, setOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const accessories = useAccessories();
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [status, setStatus] = useState({ used: 0, limit: 8, nap_mode: false });
  const [isPremium, setIsPremium] = useState(false);
  const [hovered, setHovered] = useState(false);
  // wakeNonce bumps whenever we want the WAKING video to (re)play:
  //   • component mount (page load)
  //   • tab becomes visible again
  const [wakeNonce, setWakeNonce] = useState(0);
  const bottomRef = useRef(null);

  const napMode = !!status.nap_mode;

  // Re-trigger WAKING on tab focus / visibility return.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") {
        setWakeNonce((n) => n + 1);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, []);

  // Don't render at all for logged-out users? — NO. The cat is now public.
  // Just skip the quota /status call when there is no token, and gate the
  // actual chat send behind a friendly "log in to chat" panel.
  useEffect(() => {
    if (!user) {
      setIsPremium(false);
      return;
    }
    let cancelled = false;
    apiClient()
      .get("/assistant/status")
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => {});
    apiClient()
      .get("/billing/me")
      .then((r) => { if (!cancelled) setIsPremium(!!r.data?.is_premium); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, open]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(history.slice(-20))); } catch {}
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [history]);

  // (Auth gate removed Feb 2026 — the floating cat is now visible to guests.)

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;
    // Guest tier: dictionary works via backend (which requires auth), so
    // for guests we surface a friendly "log in to chat" reply instead of
    // hitting the API and getting a 401.
    if (!user) {
      setInput("");
      setHistory((h) => [
        ...h,
        { role: "user", text },
        { role: "cat", text: t("assistant.guestPrompt"), guestPrompt: true },
      ]);
      return;
    }
    setInput("");
    setHistory((h) => [...h, { role: "user", text }]);
    setSending(true);
    try {
      const res = await apiClient().post("/assistant/ask", { message: text });
      const d = res.data || {};
      setHistory((h) => [...h, {
        role: "cat",
        text: d.answer || "🐾",
        source: d.source,
        napMode: !!d.nap_mode,
      }]);
      setStatus((s) => ({ ...s, used: d.used ?? s.used, limit: d.limit ?? s.limit, nap_mode: !!d.nap_mode }));
    } catch {
      setHistory((h) => [...h, { role: "cat", text: t("assistant.networkError") }]);
    } finally {
      setSending(false);
    }
  };

  const suggestions = lang === "tr" ? SUGGESTIONS_TR : SUGGESTIONS_EN;

  return (
    <>
      {/* Global chroma-key filter — kept at the top-level fragment so its
          DOM presence enables the url(#patiWhiteKey) reference on the
          videos, but it doesn't paint a dot artefact inside the button
          on Android WebView. */}
      <svg
        width="0"
        height="0"
        aria-hidden
        focusable="false"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          width: 0,
          height: 0,
          pointerEvents: "none",
          opacity: 0,
          overflow: "hidden",
        }}
      >
        <defs>
          <filter id="patiWhiteKey" colorInterpolationFilters="sRGB" x="0%" y="0%" width="100%" height="100%">
            <feColorMatrix
              type="matrix"
              values="
                1 0 0 0 0
                0 1 0 0 0
                0 0 1 0 0
                -6 -6 -6 0 18
              "
            />
          </filter>
        </defs>
      </svg>

      {/* Customize sheet — visual cards, one-click apply.
          Position:fixed above the badge. Desktop = badge right:48 bottom:64.
          Chat panel & sheet anchor to the same right edge, just above the
          badge (160px badge height + 24px gap). */}
      {customizeOpen && (
        <CustomizeSheet
          lang={lang}
          accessories={accessories}
          isPremium={isPremium}
          onClose={() => setCustomizeOpen(false)}
        />
      )}

      {/* Chat panel */}
      {open && !liveShutterActive && (
        <>
          {/* 23 Tem 2026: panel sadece X'e basınca kapanıyordu — mesaj
              alanının dışına (arka plana) dokununca da kapansın diye
              görünmez bir tam-ekran arka plan eklendi. Panelin kendisi
              tıklamayı durdurur (stopPropagation), yoksa panel içine her
              tıklama da kapatırdı. */}
          <div
            className="fixed inset-0"
            style={{ zIndex: 9998 }}
            onClick={() => setOpen(false)}
            data-testid="pati-asistan-backdrop"
            aria-hidden="true"
          />
          <div
            data-testid="pati-asistan-panel"
            className="fixed w-[380px] max-w-[calc(100vw-2.5rem)] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden cat-mascot-panel"
            style={{ height: "min(560px, calc(100vh - 17rem))", zIndex: 9999 }}
            onClick={(e) => e.stopPropagation()}
          >
          {/* Header — no avatar here (the floating mascot already shows
              the cat; duplicating it inside the panel feels noisy). */}
          <div className="bg-orange-50 border-b border-orange-100 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-orange-100 grid place-items-center text-orange-600 shrink-0">
              <Sparkles size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm tracking-tight truncate">
                {t("assistant.name")}
              </div>
              <div className={`text-[11px] truncate ${napMode ? "text-stone-400" : "text-emerald-600"}`}>
                {napMode ? t("assistant.napStatus") : t("assistant.readyStatus")}
              </div>
            </div>
            <button
              onClick={() => setCustomizeOpen(true)}
              data-testid="pati-asistan-customize"
              className="text-stone-500 hover:text-orange-600 rounded-full w-7 h-7 grid place-items-center hover:bg-white"
              aria-label={lang === "tr" ? "Kişiselleştir" : "Customize"}
              title={lang === "tr" ? "Kişiselleştir" : "Customize"}
            >
              <Sparkles size={15} />
            </button>
            <button
              onClick={() => setOpen(false)}
              data-testid="pati-asistan-close"
              className="text-stone-500 hover:text-stone-900 rounded-full w-7 h-7 grid place-items-center hover:bg-white"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-stone-50">
            {history.length === 0 && (
              <div className="space-y-3" data-testid="pati-asistan-suggestions">
                <p className="text-xs text-stone-500 px-1">{t("assistant.suggestionsTitle")}</p>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="block w-full text-left text-sm bg-white border border-stone-200 rounded-xl px-3 py-2 hover:border-orange-300 hover:bg-orange-50 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {history.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] text-sm rounded-2xl px-3 py-2 leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-orange-600 text-white"
                      : "bg-white border border-stone-200 text-stone-800"
                  }`}
                >
                  {m.text}
                  {m.guestPrompt && (
                    <a
                      href="/login"
                      className="block mt-2 text-orange-700 font-semibold text-xs hover:underline"
                      data-testid="pati-asistan-guest-login"
                    >
                      {t("assistant.guestLoginCta")} →
                    </a>
                  )}
                  {m.role === "cat" && m.source && !m.napMode && !m.guestPrompt && (
                    <div className="mt-1 text-[9px] uppercase tracking-widest text-stone-400">
                      {m.source === "dictionary" ? "FAQ" : m.source === "cache" ? "Cache" : "AI"}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-stone-200 rounded-2xl px-3 py-2 text-sm text-stone-500">
                  <span className="inline-block w-2 h-2 bg-orange-400 rounded-full animate-bounce" />
                  <span className="inline-block w-2 h-2 bg-orange-400 rounded-full animate-bounce mx-1" style={{ animationDelay: "0.15s" }} />
                  <span className="inline-block w-2 h-2 bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-stone-200 p-3 flex items-center gap-2 bg-white"
          >
            <input
              data-testid="pati-asistan-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending || napMode}
              placeholder={napMode ? t("assistant.napHint") : t("assistant.placeholder")}
              // text-base (16px) is deliberate: iOS Safari auto-zooms the page when a
              // focused input has font-size < 16px, and it never zooms back out — the
              // user is left stranded on a magnified page. Do not lower this to text-sm.
              className="flex-1 text-base bg-stone-100 rounded-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300 disabled:opacity-50"
              maxLength={500}
            />
            <button
              type="submit"
              data-testid="pati-asistan-send"
              disabled={!input.trim() || sending || napMode}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-stone-300 text-white rounded-full w-9 h-9 grid place-items-center transition-colors"
              aria-label="Send"
            >
              <Send size={15} />
            </button>
          </form>
          <div className="text-[10px] text-stone-400 px-3 pb-2 -mt-1">
            {user ? `${status.used}/${status.limit} · ` : ""}{t("assistant.disclaimer")}
          </div>
        </div>
        </>
      )}

      {/* Embedded mascot — sits inline inside the bottom dock (between
          Mesajlar and Profil). Always visible; tapping it toggles the
          chat panel above the dock. When live camera is active, show
          a shutter FAB instead. */}
      <div data-testid="pati-asistan" className="contents">
        {liveShutterActive ? (
          <button
            type="button"
            onClick={() => {
              if (!liveShutterDisabled) triggerShutter();
            }}
            disabled={liveShutterDisabled}
            aria-label={lang === "tr" ? "Şimdi Çek" : "Capture now"}
            data-testid="pati-asistan-live-shutter"
            className="cat-mascot-badge cat-mascot-badge--sm inline-flex flex-col items-center justify-center gap-0.5 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:pointer-events-none text-white font-bold text-[11px] shadow-lg shadow-orange-900/20 ring-2 ring-white/70 animate-pulse motion-reduce:animate-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
            style={{
              position: "fixed",
              right: "12px",
              bottom: "calc(env(safe-area-inset-bottom, 0px) + 88px)",
              zIndex: 41,
            }}
          >
            <Camera size={22} aria-hidden="true" />
            <span>{lang === "tr" ? "Şimdi Çek" : "Capture now"}</span>
          </button>
        ) : (
          <CatAvatar
            napMode={napMode}
            thinking={sending}
            accessories={accessories}
            hovered={hovered}
            standPose={open}
            wakeNonce={wakeNonce}
            onClick={() => setOpen((o) => !o)}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
            ariaLabel={t("assistant.name")}
            testId="pati-asistan-toggle"
          />
        )}
      </div>
    </>
  );
}
